// Draws property pins on a Google map.
//
// The dataset is ~86k points, which is far too many Marker instances to hold
// at once. Instead every pin lives in a plain array, and on each idle we take
// only what is inside the viewport and bucket it into a grid sized so clusters
// land roughly 60px apart. That leaves a few hundred markers on the map at any
// time regardless of how many properties matched.

// A pin with no status flag: a small dot in Summit's brand blue, the same as
// the parcel highlight the dots hand over to at parcel zoom. The grey dots
// this map started with were invisible over the imagery.
const PIN_COLOR = '#90B9ED';

// The map is dark, so pins are outlined against the imagery rather than in
// white, and clusters use the same blue as the rest of the view's controls.
const PIN_STROKE = '#0B0D0E';
const CLUSTER_COLOR = '#1570EF';
const CLUSTER_SPACING_PX = 60;
const TILE_SIZE = 256;

// The territory list's flag colours (flag-pin-*.svg, ticket 30524), as the client
// asked, and the neon green the client asked for Summit's own listings; the rim and the
// dot are the same colour darker, as on Google's pin.
const FLAG_COLORS = { neon: '#39FF14', green: '#22C55E', red: '#EF4444', orange: '#FB923C' };
const FLAG_RIMS = { neon: '#1F9E0A', green: '#15803D', red: '#B91C1C', orange: '#C2410C' };
const FLAG_CORES = { neon: '#0E4A05', green: '#14532D', red: '#7F1D1D', orange: '#7C2D12' };

// Counts sit on the rim colour, dark enough for a white number. Neon's rim is
// too near green's to tell apart, so its counts keep the neon with a dark number.
const CLUSTER_FILLS = { ...FLAG_RIMS, neon: FLAG_COLORS.neon };
const CLUSTER_TEXT = { neon: PIN_STROKE };

// Google's pin a little smaller — the client's words. The tip is the property's point.
const FLAG_PIN_WIDTH = 24;
const FLAG_PIN_HEIGHT = 34;
const FLAG_PIN_TIP_Y = 31;

// Only the drawn drop takes the pointer, not the empty corners of its box.
const FLAG_PIN_SHAPE = { type: 'poly', coords: [12, 31, 4, 20, 2, 12, 5, 4, 12, 1, 19, 4, 22, 12, 20, 20] };

// Above any blue cluster, whose zIndex grows with its count; Summit's listings
// over green over red over orange where two stand on the same point, and
// badges over all of them.
const FLAGGED_Z_BASE = 1000000;
const FLAG_Z = { neon: 4, green: 3, red: 2, orange: 1 };
const BADGE_Z = 10;

const NO_FLAG = 'none';
const KINDS = [NO_FLAG, 'neon', 'green', 'red', 'orange'];

// A colour's count on another cluster's rim: upper left, upper right, right, lower right.
const BADGE_SCALE = 8.5;
const BADGE_ANGLES = { neon: (-2 * Math.PI) / 3, green: -Math.PI / 3, red: 0, orange: Math.PI / 3 };

export default class PinLayer {
    constructor(map, { onSelect, onSelectMatch, onSelectMatches }) {
        this.map = map;
        this.onSelect = onSelect;
        this.onSelectMatch = onSelectMatch;
        this.onSelectMatches = onSelectMatches;
        this.pins = [];
        // The category filter's matches, or null while it is off.
        this.matchIds = null;
        this.markers = [];
        this.interactive = true;
        this.overParcels = false;
        this.listener = map.addListener('idle', () => this.draw());
    }

    /** @param {Array<{id: string, lat: number, lng: number, flag?: string}>} pins the flag as the server works it out */
    setPins(pins) {
        this.pins = pins;
        this.draw();
    }

    /**
     * Only these pins while the category filter is on, and a click on one
     * zooms onto it rather than opening it where it stands.
     *
     * @param {Set<string>|null} matchIds null shows every pin again
     */
    setMatches(matchIds) {
        this.matchIds = matchIds;
        this.draw();
    }

    /**
     * Over drawn outlines a blue dot is duplicate ink, but a flag says what
     * the outline does not: flagged pins stay, each on its own.
     */
    setOverParcels(overParcels) {
        if (this.overParcels === overParcels) return;

        this.overParcels = overParcels;
        this.draw();
    }

    /**
     * Markers sit above the map and swallow the pointer, so drawing a shape
     * that starts on top of one would never reach the map's own mouse events.
     */
    setInteractive(interactive) {
        this.interactive = interactive;
        this.markers.forEach((marker) => marker.setOptions({ clickable: interactive }));
    }

    destroy() {
        if (this.listener) this.listener.remove();
        this.clear();
    }

    clear() {
        this.markers.forEach((marker) => marker.setMap(null));
        this.markers = [];
    }

    draw() {
        const bounds = this.map.getBounds();
        if (!bounds || this.pins.length === 0) return;

        const cell = (CLUSTER_SPACING_PX * 360) / (TILE_SIZE * Math.pow(2, this.map.getZoom()));
        const cells = new Map();

        this.pins.forEach((pin) => {
            if (this.matchIds && !this.matchIds.has(pin.id)) return;
            if (this.overParcels && !pin.flag) return;
            if (!bounds.contains({ lat: pin.lat, lng: pin.lng })) return;

            const key = this.overParcels ? pin.id : Math.floor(pin.lat / cell) + ':' + Math.floor(pin.lng / cell);
            const kind = pin.flag || NO_FLAG;
            const groups = cells.get(key) || {};

            groups[kind] = (groups[kind] || []).concat(pin);
            cells.set(key, groups);
        });

        this.clear();
        cells.forEach((groups) => this.markers.push(...this.createMarkers(groups)));
    }

    /**
     * One cell's markers. Each colour keeps its own count, so a flag is never
     * lost inside a blue one; the counts sharing a cell ride as badges on the
     * biggest cluster's rim, where stacked on it they hid each other.
     */
    createMarkers(groups) {
        const clusters = KINDS.filter((kind) => groups[kind] && groups[kind].length > 1);
        const host = clusters.indexOf(NO_FLAG) >= 0
            ? NO_FLAG
            : clusters.sort((a, b) => groups[b].length - groups[a].length)[0];

        return KINDS.filter((kind) => groups[kind]).map((kind) => {
            const group = groups[kind];

            if (group.length === 1) return this.createPin(group[0]);

            return this.createCluster(group, kind === host ? null : { group: groups[host], kind: host });
        });
    }

    // Markers are not `optimized`: Google then draws image pins on a layer of
    // their own, under the circle clusters whatever their zIndex.
    createPin(pin) {
        // Over the parcels the map is already close, and the match click's
        // zoom to 18 would pull it back out from 19 or 20.
        const isFiltered = this.matchIds !== null && !this.overParcels;
        const marker = new google.maps.Marker({
            map: this.map,
            position: { lat: pin.lat, lng: pin.lng },
            clickable: this.interactive,
            optimized: false,
            zIndex: pin.flag ? FLAGGED_Z_BASE + FLAG_Z[pin.flag] : 5,
            icon: pin.flag ? flagPinIcon(pin.flag) : {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: PIN_COLOR,
                fillOpacity: 1,
                strokeColor: PIN_STROKE,
                strokeWeight: 1.5,
                scale: 5.5,
            },
            shape: pin.flag ? FLAG_PIN_SHAPE : undefined,
        });

        marker.addListener('click', () => (isFiltered ? this.onSelectMatch(pin) : this.onSelect(pin)));
        return marker;
    }

    /**
     * A count, drawn at its own pins' middle or, as a badge, on the host
     * cluster's rim at its colour's fixed place.
     */
    createCluster(group, host) {
        const isFiltered = this.matchIds !== null;
        const flag = group[0].flag;
        const center = middleOf(group);
        const hostScale = host ? clusterScale(host.group.length) : 0;
        const scale = host ? BADGE_SCALE : clusterScale(group.length);
        const angle = host ? BADGE_ANGLES[flag] : 0;
        const reach = host ? hostScale + scale / 2 : 0;

        const marker = new google.maps.Marker({
            map: this.map,
            position: host ? middleOf(host.group) : center,
            clickable: this.interactive,
            optimized: false,
            zIndex: flag ? FLAGGED_Z_BASE + FLAG_Z[flag] + (host ? BADGE_Z : 0) : 5 + group.length,
            label: { text: String(group.length), color: CLUSTER_TEXT[flag] || '#fff', fontSize: host ? '10px' : '11px', fontWeight: '600' },
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: flag ? CLUSTER_FILLS[flag] : CLUSTER_COLOR,
                fillOpacity: 0.95,
                strokeColor: '#0B0D0E',
                strokeWeight: host ? 1.5 : 2,
                scale,
                // In path units: moves the circle and its count off the host's centre.
                anchor: new google.maps.Point(-(reach * Math.cos(angle)) / scale, -(reach * Math.sin(angle)) / scale),
            },
        });

        marker.addListener('click', () => {
            if (isFiltered) {
                this.onSelectMatches(group);
                return;
            }

            this.map.setCenter(center);
            this.map.setZoom(Math.min(20, this.map.getZoom() + 3));
        });

        return marker;
    }
}

function middleOf(group) {
    return group.reduce(
        (sum, pin) => ({ lat: sum.lat + pin.lat / group.length, lng: sum.lng + pin.lng / group.length }),
        { lat: 0, lng: 0 }
    );
}

function clusterScale(count) {
    return Math.min(22, 12 + Math.log10(count) * 6);
}

const flagPinUrls = {};
const flagPinIcons = {};

/**
 * The flag's pin as an image, for the legend too. A Symbol fills one colour
 * only, so it is an SVG, built once per flag: markers are re-made on every pan.
 */
export function flagPinUrl(flag) {
    if (!flagPinUrls[flag]) {
        const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + FLAG_PIN_WIDTH + '" height="' + FLAG_PIN_HEIGHT
            + '" viewBox="0 0 24 34">'
            + '<defs><filter id="s" x="-30%" y="-20%" width="160%" height="150%">'
            + '<feDropShadow dx="0" dy="1" stdDeviation="1" flood-opacity=".45"/></filter></defs>'
            + '<g filter="url(#s)">'
            + '<path d="M12 31C12 31 3.9 21.9 2.4 16C2 14.6 1.8 13.2 1.8 11.5C1.8 5.87 6.37 1.3 12 1.3C17.63 1.3 22.2 5.87 22.2 11.5'
            + 'C22.2 13.2 22 14.6 21.6 16C20.1 21.9 12 31 12 31Z" fill="' + FLAG_COLORS[flag] + '" stroke="' + FLAG_RIMS[flag]
            + '" stroke-width="1" stroke-linejoin="round"/>'
            + '<circle cx="12" cy="11.5" r="3.9" fill="' + FLAG_CORES[flag] + '"/>'
            + '</g></svg>';

        flagPinUrls[flag] = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
    }

    return flagPinUrls[flag];
}

function flagPinIcon(flag) {
    if (!flagPinIcons[flag]) {
        flagPinIcons[flag] = {
            url: flagPinUrl(flag),
            scaledSize: new google.maps.Size(FLAG_PIN_WIDTH, FLAG_PIN_HEIGHT),
            anchor: new google.maps.Point(FLAG_PIN_WIDTH / 2, FLAG_PIN_TIP_Y),
        };
    }

    return flagPinIcons[flag];
}
